/**
 * sessionStorage-backed Dashboard cache.
 *
 * Keyed by eventId. Survives soft navigations within the same tab so the
 * second visit to /home (or /events/:id) renders instantly while a
 * background refetch validates. Cleared on logout (AuthContext).
 *
 * Lives in /lib so it can be unit-tested without spinning up React.
 */

const PREFIX = 'butler.dashboard.v1'
const MAX_AGE_MS = 60 * 60 * 1000 // 1 hour — older than this we skip the cache

function key(eventId: number, date?: string | null) {
  return `${PREFIX}:${eventId}${date ? `:${date}` : ''}`
}

export interface CachedEntry<T> {
  data: T
  cachedAt: number
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.sessionStorage
  } catch {
    return null
  }
}

export function readDashboardCache<T>(eventId: number, date?: string | null): T | null {
  const store = safeStorage()
  if (!store) return null
  try {
    const raw = store.getItem(key(eventId, date))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedEntry<T>
    if (!parsed || typeof parsed.cachedAt !== 'number') return null
    if (Date.now() - parsed.cachedAt > MAX_AGE_MS) {
      store.removeItem(key(eventId, date))
      return null
    }
    return parsed.data
  } catch {
    return null
  }
}

export function writeDashboardCache<T>(eventId: number, data: T, date?: string | null): void {
  const store = safeStorage()
  if (!store) return
  try {
    const entry: CachedEntry<T> = { data, cachedAt: Date.now() }
    store.setItem(key(eventId, date), JSON.stringify(entry))
  } catch {
    // Quota exceeded or storage disabled — silently skip; next render will refetch.
  }
}

export function clearDashboardCache(eventId?: number): void {
  const store = safeStorage()
  if (!store) return
  try {
    if (typeof eventId === 'number') {
      store.removeItem(key(eventId))
      return
    }
    // Clear every dashboard entry — used on logout.
    const toRemove: string[] = []
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i)
      if (k && k.startsWith(`${PREFIX}:`)) toRemove.push(k)
    }
    toRemove.forEach((k) => store.removeItem(k))
  } catch {
    // Nothing to do.
  }
}

/**
 * Fire-and-forget prefetch — used by EventSwitcher hover + AuthProvider on
 * post-login event-list load. Returns a Promise that resolves regardless
 * of outcome; callers ignore the return value.
 */
export async function prefetchDashboard(
  eventId: number,
  fetchImpl: typeof fetch = typeof fetch !== 'undefined' ? fetch : (undefined as unknown as typeof fetch),
): Promise<void> {
  if (!fetchImpl || !Number.isFinite(eventId)) return
  try {
    const res = await fetchImpl(`/api/dashboard/${eventId}/stats`, {
      credentials: 'same-origin',
    })
    if (!res.ok) return
    const data = await res.json()
    writeDashboardCache(eventId, data)
  } catch {
    // Prefetch failures are silent — they're best-effort.
  }
}
